import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import {
  FilterFieldOption,
  FilterPredicate,
  FilterPredicateOp,
} from '@internal/backstage-plugin-bidone-backend';
import {
  FilterPreviewInput,
  useFilterOptions,
  useFilterPreview,
} from '../../hooks/useCampaigns';

// Operators offered per field value type — mirrors what the backend's
// evaluatePredicate can meaningfully apply.
const OPS_BY_TYPE: Record<string, FilterPredicateOp[]> = {
  string: ['eq', 'neq', 'in', 'notIn', 'contains', 'notContains', 'exists', 'notExists'],
  number: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'exists', 'notExists'],
  boolean: ['eq', 'neq', 'exists', 'notExists'],
  array: ['contains', 'notContains', 'in', 'notIn', 'exists', 'notExists'],
};

const OP_LABELS: Record<FilterPredicateOp, string> = {
  eq: '=',
  neq: '≠',
  gt: '>',
  gte: '≥',
  lt: '<',
  lte: '≤',
  in: 'in',
  notIn: 'not in',
  contains: 'contains',
  notContains: "doesn't contain",
  exists: 'exists',
  notExists: "doesn't exist",
};

const NO_VALUE_OPS: FilterPredicateOp[] = ['exists', 'notExists'];
const MULTI_VALUE_OPS: FilterPredicateOp[] = ['in', 'notIn'];

// Stable identity for a field option so it can be matched back from a stored
// predicate.
const fieldId = (f: FilterFieldOption): string =>
  f.kind === 'entity' ? `entity:${f.path}` : `fact:${f.factId}.${f.factKey}`;

const predicateFieldId = (p: FilterPredicate): string =>
  p.kind === 'entity' ? `entity:${p.path}` : `fact:${p.factId}.${p.factKey}`;

const emptyPredicateFor = (field: FilterFieldOption): FilterPredicate => {
  const op = OPS_BY_TYPE[field.valueType][0];
  return field.kind === 'entity'
    ? { kind: 'entity', path: field.path!, op, value: undefined }
    : {
        kind: 'fact',
        factId: field.factId!,
        factKey: field.factKey!,
        op,
        value: undefined,
      };
};

const coerceScalar = (
  raw: string,
  valueType: FilterFieldOption['valueType'],
): string | number | boolean => {
  if (valueType === 'number') {
    const n = Number(raw);
    return Number.isNaN(n) ? raw : n;
  }
  if (valueType === 'boolean') return raw === 'true';
  return raw;
};

const useDebounced = <T,>(value: T, delayMs: number): T => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const handle = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(handle);
  }, [value, delayMs]);
  return debounced;
};

type PredicateRowProps = {
  predicate: FilterPredicate;
  fields: FilterFieldOption[];
  onChange: (next: FilterPredicate) => void;
  onRemove: () => void;
};

const PredicateRow = ({
  predicate,
  fields,
  onChange,
  onRemove,
}: PredicateRowProps) => {
  const field =
    fields.find(f => fieldId(f) === predicateFieldId(predicate)) ?? null;
  const valueType = field?.valueType ?? 'string';
  const ops = OPS_BY_TYPE[valueType];
  const needsValue = !NO_VALUE_OPS.includes(predicate.op);
  const isMulti = MULTI_VALUE_OPS.includes(predicate.op);
  const suggestions = (field?.values ?? []).map(String);

  const handleOpChange = (op: FilterPredicateOp) => {
    const clearValue =
      NO_VALUE_OPS.includes(op) ||
      MULTI_VALUE_OPS.includes(op) !== MULTI_VALUE_OPS.includes(predicate.op);
    onChange({ ...predicate, op, value: clearValue ? undefined : predicate.value });
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        marginBottom: 1,
      }}
    >
      <Autocomplete
        sx={{ flex: 3, minWidth: 220 }}
        size="small"
        options={fields}
        groupBy={f => f.group}
        getOptionLabel={f => f.label}
        isOptionEqualToValue={(a, b) => fieldId(a) === fieldId(b)}
        value={field}
        onChange={(_e, next) => {
          // Ignore clear events — a predicate always points at some field.
          if (next) onChange(emptyPredicateFor(next));
        }}
        renderInput={params => (
          <TextField {...params} label="Field" variant="outlined" />
        )}
      />
      <TextField
        sx={{ flex: 1.2, minWidth: 130 }}
        label="Operator"
        variant="outlined"
        size="small"
        select
        value={predicate.op}
        onChange={e => handleOpChange(e.target.value as FilterPredicateOp)}
      >
        {ops.map(op => (
          <MenuItem key={op} value={op}>
            {OP_LABELS[op]}
          </MenuItem>
        ))}
      </TextField>
      {needsValue && isMulti && (
        <Autocomplete
          sx={{ flex: 3, minWidth: 220 }}
          size="small"
          multiple
          freeSolo
          options={suggestions}
          value={
            Array.isArray(predicate.value) ? predicate.value.map(String) : []
          }
          onChange={(_e, next) =>
            onChange({
              ...predicate,
              value: next.map(v => coerceScalar(String(v), valueType)),
            })
          }
          renderTags={(selected, getTagProps) =>
            selected.map((option, index) => (
              <Chip
                size="small"
                label={option}
                {...getTagProps({ index })}
              />
            ))
          }
          renderInput={params => (
            <TextField {...params} label="Values" variant="outlined" />
          )}
        />
      )}
      {needsValue && !isMulti && valueType === 'boolean' && (
        <TextField
          sx={{ flex: 3, minWidth: 220 }}
          label="Value"
          variant="outlined"
          size="small"
          select
          value={predicate.value === undefined ? '' : String(predicate.value)}
          onChange={e =>
            onChange({ ...predicate, value: e.target.value === 'true' })
          }
        >
          <MenuItem value="true">true</MenuItem>
          <MenuItem value="false">false</MenuItem>
        </TextField>
      )}
      {needsValue && !isMulti && valueType !== 'boolean' && (
        <Autocomplete
          sx={{ flex: 3, minWidth: 220 }}
          size="small"
          freeSolo
          options={suggestions}
          value={predicate.value === undefined ? '' : String(predicate.value)}
          onInputChange={(_e, next) =>
            onChange({
              ...predicate,
              value: next === '' ? undefined : coerceScalar(next, valueType),
            })
          }
          renderInput={params => (
            <TextField {...params} label="Value" variant="outlined" />
          )}
        />
      )}
      <IconButton size="small" onClick={onRemove} aria-label="Remove condition">
        <DeleteIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};

export type FilterDraft = {
  combine: 'all' | 'any';
  predicates: FilterPredicate[];
};

type FilterBuilderProps = {
  value: FilterDraft;
  onChange: (next: FilterDraft) => void;
  snapshotMode?: boolean;
  onSnapshotSelectionChange?: (refs: string[]) => void;
};

// Row-based predicate builder with a live "Matches N components" indicator.
// Fields are grouped by source (Catalog / Annotations / fact retrievers),
// operators narrow by the field's value type, and values autocomplete from
// the live portfolio via /filters/options.
export const FilterBuilder = ({
  value,
  onChange,
  snapshotMode,
  onSnapshotSelectionChange,
}: FilterBuilderProps) => {
  const { data: options, error: optionsError } = useFilterOptions();
  const [showMatches, setShowMatches] = useState(false);
  const [snapshotSelection, setSnapshotSelection] = useState<Set<string>>(new Set());

  const debouncedValue = useDebounced(value, 400);
  const previewInput = useMemo<FilterPreviewInput>(
    () => ({
      combine: debouncedValue.combine,
      predicates: debouncedValue.predicates,
    }),
    [debouncedValue],
  );
  const { data: preview, isFetching: previewFetching } =
    useFilterPreview(previewInput);

  const fields = options?.fields ?? [];

  // When snapshot mode turns on, seed selection from current preview; when off, clear.
  useEffect(() => {
    if (snapshotMode) {
      if (preview) {
        const initial = new Set(preview.matches.map(m => m.ref));
        setSnapshotSelection(initial);
        onSnapshotSelectionChange?.(Array.from(initial));
      }
    } else {
      setSnapshotSelection(new Set());
      onSnapshotSelectionChange?.([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshotMode]);

  // When the preview updates while in snapshot mode, add newly matched refs
  // without removing ones the user has already unchecked.
  useEffect(() => {
    if (!snapshotMode || !preview) return;
    setSnapshotSelection(prev => {
      let changed = false;
      const next = new Set(prev);
      preview.matches.forEach(m => {
        if (!next.has(m.ref)) { next.add(m.ref); changed = true; }
      });
      if (changed) {
        onSnapshotSelectionChange?.(Array.from(next));
        return next;
      }
      return prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview, snapshotMode]);

  const toggleSnapshot = (ref: string) => {
    setSnapshotSelection(prev => {
      const next = new Set(prev);
      if (next.has(ref)) next.delete(ref); else next.add(ref);
      onSnapshotSelectionChange?.(Array.from(next));
      return next;
    });
  };

  const selectAll = () => {
    const all = new Set((preview?.matches ?? []).map(m => m.ref));
    setSnapshotSelection(all);
    onSnapshotSelectionChange?.(Array.from(all));
  };

  const deselectAll = () => {
    setSnapshotSelection(new Set());
    onSnapshotSelectionChange?.([]);
  };

  const listOpen = !!snapshotMode || showMatches;

  return (
    <div>
      {optionsError ? (
        <Alert severity="warning">
          Could not load field suggestions: {(optionsError as Error).message}
        </Alert>
      ) : null}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          marginBottom: 1.5,
        }}
      >
        <Typography variant="body2">Component must match</Typography>
        <TextField
          sx={{ width: 90 }}
          select
          size="small"
          variant="outlined"
          value={value.combine}
          onChange={e =>
            onChange({ ...value, combine: e.target.value as 'all' | 'any' })
          }
        >
          <MenuItem value="all">all</MenuItem>
          <MenuItem value="any">any</MenuItem>
        </TextField>
        <Typography variant="body2">of the conditions:</Typography>
      </Box>

      {value.predicates.length === 0 && (
        <Typography variant="body2" color="textSecondary" gutterBottom>
          No conditions — the filter matches every component.
        </Typography>
      )}
      {value.predicates.map((p, index) => (
        <PredicateRow
          key={index}
          predicate={p}
          fields={fields}
          onChange={next =>
            onChange({
              ...value,
              predicates: value.predicates.map((existing, i) =>
                i === index ? next : existing,
              ),
            })
          }
          onRemove={() =>
            onChange({
              ...value,
              predicates: value.predicates.filter((_p, i) => i !== index),
            })
          }
        />
      ))}
      <Button
        size="small"
        startIcon={<AddIcon />}
        onClick={() => {
          if (fields.length === 0) return;
          onChange({
            ...value,
            predicates: [...value.predicates, emptyPredicateFor(fields[0])],
          });
        }}
        disabled={fields.length === 0}
      >
        Add condition
      </Button>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          marginTop: 1.5,
        }}
      >
        <Chip
          size="small"
          color="primary"
          label={
            preview
              ? `Matches ${preview.count} of ${preview.total} components${
                  previewFetching ? '…' : ''
                }`
              : 'Calculating matches…'
          }
        />
        {!snapshotMode && preview && preview.count > 0 && (
          <IconButton
            size="small"
            onClick={() => setShowMatches(s => !s)}
            aria-label="Show matched components"
          >
            {showMatches ? (
              <ExpandLessIcon fontSize="small" />
            ) : (
              <ExpandMoreIcon fontSize="small" />
            )}
          </IconButton>
        )}
      </Box>

      {snapshotMode && preview && preview.count > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
          <Button size="small" onClick={selectAll}>Select all</Button>
          <Button size="small" onClick={deselectAll}>Deselect all</Button>
          <Typography variant="caption" color="textSecondary" sx={{ ml: 'auto' }}>
            {snapshotSelection.size} of {preview.count} selected
          </Typography>
        </Box>
      )}

      <Collapse in={listOpen && !!preview}>
        <Box
          sx={theme => ({
            marginTop: theme.spacing(1),
            maxHeight: snapshotMode ? 320 : 180,
            overflowY: 'auto',
            background: theme.palette.action.hover,
            borderRadius: theme.shape.borderRadius,
          })}
        >
          {snapshotMode ? (
            <List dense disablePadding>
              {(preview?.matches ?? []).map(m => (
                <ListItem key={m.ref} disablePadding>
                  <ListItemButton dense onClick={() => toggleSnapshot(m.ref)}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Checkbox
                        edge="start"
                        size="small"
                        checked={snapshotSelection.has(m.ref)}
                        tabIndex={-1}
                        disableRipple
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={m.title}
                      secondary={m.owner ?? undefined}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          ) : (
            <Box sx={theme => ({ padding: theme.spacing(1) })}>
              {(preview?.matches ?? []).map(m => (
                <Typography key={m.ref} variant="body2">
                  {m.title}
                  {m.owner ? (
                    <Typography component="span" variant="caption" color="textSecondary">
                      {' '}
                      — {m.owner}
                    </Typography>
                  ) : null}
                </Typography>
              ))}
            </Box>
          )}
        </Box>
      </Collapse>
    </div>
  );
};
